import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Select } from '@/components/ui'
import { useAuthStore } from '@/features/auth/authStore'
import { useCompanyList } from '@/features/company'
import { useDashboardStore } from '@/features/dashboard'
import {
  DeliverableLevel,
  DetailDrawer,
  OrgLevel,
  PeriodMetric,
  ProjectLevel,
} from '@/features/dashboard/components'
import { ViewLevel } from '@/features/dashboard/constants'
import { useOrgLevelMetric } from '@/features/dashboard/hooks/useOrgLevelMetric'
import { useDeliverableSetList } from '@/features/deliverable-set'
import { ERoleType } from '@/features/user-management/constants'
import { ResponseCompany, ResponseDeliverableSet } from '@/schema'
import { DEFAULT_GET_ALL_LIMIT, SortDirection } from '@/utils/constants'

const DashboardPage: React.FC = () => {
  const { t } = useTranslation()
  const { filters, setFilter, viewLevel, setViewLevel, reset } =
    useDashboardStore()

  const roles = useAuthStore((state) => state.roles)
  const getCompaniesFromRoles = useAuthStore(
    (state) => state.getCompaniesFromRoles
  )
  const getListProjectUserHasAccess = useAuthStore(
    (state) => state.getListProjectUserHasAccess
  )

  const isProjectLead = useMemo(() => {
    const hasProjectLeadRole = roles?.some(
      (role) => role.role_name === ERoleType.PROJECT_LEAD
    )
    const hasOrgAdminRole = roles?.some(
      (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
    )
    // Org admins have full access, so they should not be treated as project leads
    return hasProjectLeadRole && !hasOrgAdminRole
  }, [roles])

  const accessibleProjectIds = useMemo(() => {
    return getListProjectUserHasAccess()
  }, [getListProjectUserHasAccess])

  useEffect(() => {
    return () => {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const {
    projectMetric: { data: projectsData = [] } = {},
    isLoadingNoOfProjects,
  } = useOrgLevelMetric()

  const {
    data: { data: companiesData = [] } = {},
    isLoading: isCompaniesLoading,
  } = useCompanyList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'name', direction: SortDirection.ASC },
    enabled: !isProjectLead,
  })

  const {
    data: { data: deliverableSetsData = [] } = {},
    isLoading: isDeliverableSetsLoading,
  } = useDeliverableSetList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'deliverable_set_name', direction: SortDirection.ASC },
    filters: { projectId: filters.projectId || '' },
    enabled: !!filters.projectId,
  })

  const companyOptions = useMemo(() => {
    if (!isProjectLead)
      return [
        { value: 'all', label: t('dashboard.filters.allCompanies') },
        ...(companiesData || []).map((company: ResponseCompany) => ({
          value: company.id,
          label: company.name,
        })),
      ]

    // For Project Lead, get companies from roles instead of API
    const companiesFromRoles = getCompaniesFromRoles()
    return companiesFromRoles.map((company) => ({
      value: company.id,
      label: company.name,
    }))
  }, [companiesData, isProjectLead, t, getCompaniesFromRoles])

  const projectOptions = useMemo(() => {
    if (!isProjectLead)
      return [
        { value: 'all', label: t('dashboard.filters.allProjects') },
        ...(projectsData || []).map((project) => ({
          value: project.project_id,
          label: project.project_name,
        })),
      ]

    const filteredProjects = (projectsData || []).filter((project) =>
      accessibleProjectIds.includes(project.project_id)
    )

    const projectList = filteredProjects.map((project) => ({
      value: project.project_id,
      label: project.project_name,
    }))

    return projectList
  }, [projectsData, accessibleProjectIds, isProjectLead, t])

  useEffect(() => {
    if (!isProjectLead) return

    const isCurrentCompanyValid =
      filters.companyId &&
      companyOptions.some((opt) => opt.value === filters.companyId)

    if (!isCurrentCompanyValid && companyOptions.length > 0) {
      const firstCompanyId = companyOptions[0].value

      setFilter({
        companyId: firstCompanyId,
        projectId: null,
        deliverableSetId: null,
      })
    }
  }, [isProjectLead, companyOptions, filters.companyId, setFilter])

  useEffect(() => {
    if (!isProjectLead) return
    if (!filters.companyId) return

    const isCurrentProjectValid =
      filters.projectId &&
      projectOptions.some((opt) => opt.value === filters.projectId)

    if (!isCurrentProjectValid && projectOptions.length > 0) {
      const firstProjectId = projectOptions[0].value
      setFilter({
        projectId: firstProjectId,
        deliverableSetId: null,
      })
    }
  }, [
    isProjectLead,
    projectOptions,
    filters.companyId,
    filters.projectId,
    setFilter,
  ])

  useEffect(() => {
    if (filters.deliverableSetId) {
      setViewLevel(ViewLevel.DELIVERABLE_SETS)
    } else if (filters.projectId) {
      setViewLevel(ViewLevel.PROJECTS)
    } else {
      setViewLevel(ViewLevel.ORG)
    }
  }, [filters.projectId, filters.deliverableSetId, setViewLevel])

  return (
    <div className="flex flex-col gap-6 h-[calc(100%+2rem)] -mb-8">
      {/* MARK: Page Title */}
      <div className="flex justify-between flex-shrink-0">
        <div className="space-y-1">
          <h1 className="m-0 font-semibold text-display-xs text-primary-900">
            {t('dashboard.title')}
          </h1>
          <p className="m-0 text-md text-tertiary-600">
            {t('dashboard.description')}
          </p>
        </div>
      </div>
      <div className="flex gap-3 flex-shrink-0">
        <div className="space-y-1.5">
          <label
            htmlFor="company"
            className="text-sm font-medium text-secondary-700"
          >
            Company
          </label>
          <Select
            searchable
            clearable={!isProjectLead}
            disabled={isCompaniesLoading || companyOptions.length === 0}
            value={filters.companyId || ''}
            placeholder={t(
              isProjectLead
                ? 'dashboard.filters.selectCompany'
                : 'dashboard.filters.allCompanies'
            )}
            maxHeight={600}
            onChange={(value) =>
              setFilter({
                companyId: value === 'all' || !value ? null : value,
                projectId: null,
                deliverableSetId: null,
              })
            }
            options={companyOptions}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="project"
            className="text-sm font-medium text-secondary-700"
          >
            Project
          </label>
          <Select
            searchable
            clearable={!isProjectLead}
            disabled={
              !filters.companyId ||
              isLoadingNoOfProjects ||
              projectOptions.length === 0
            }
            value={filters.projectId || ''}
            placeholder={t(
              isProjectLead
                ? 'dashboard.filters.selectProject'
                : 'dashboard.filters.allProjects'
            )}
            maxHeight={600}
            onChange={(value) =>
              setFilter({
                projectId: value === 'all' || !value ? null : value,
                deliverableSetId: null,
              })
            }
            options={projectOptions}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="deliverableSet"
            className="text-sm font-medium text-secondary-700"
          >
            Deliverable Set
          </label>
          <Select
            searchable
            clearable
            disabled={!filters.projectId || isDeliverableSetsLoading}
            value={filters.deliverableSetId || ''}
            placeholder={t('dashboard.filters.allDeliverableSets')}
            maxHeight={600}
            onChange={(value) =>
              setFilter({
                deliverableSetId: value === 'all' || !value ? null : value,
              })
            }
            options={[
              {
                value: 'all',
                label: t('dashboard.filters.allDeliverableSets'),
              },
              ...(deliverableSetsData || []).map(
                (deliverableSet: ResponseDeliverableSet) => ({
                  value: deliverableSet.id,
                  label: deliverableSet.deliverable_set_name,
                })
              ),
            ]}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
        </div>
      </div>
      <div className="p-8 bg-secondary -mx-8 flex-1 space-y-5 border-t border-secondary">
        {viewLevel === ViewLevel.ORG && !isProjectLead && (
          <>
            <OrgLevel />
            <PeriodMetric />
          </>
        )}
        {viewLevel === ViewLevel.PROJECTS && <ProjectLevel />}
        {viewLevel === ViewLevel.DELIVERABLE_SETS && <DeliverableLevel />}
      </div>
      <DetailDrawer />
    </div>
  )
}

export default DashboardPage
